/**
 * One-time localStorage key migration for the Tawreed → Sparts rebrand
 * (May 2026).
 *
 * The rebrand renamed two persisted keys:
 *   - `tawreed.session.user` → `sparts.session.user`  (optimistic auth restore)
 *   - `tawreed.lang`         → `sparts.lang`           (i18n language preference)
 *
 * Without this shim, a returning user would briefly flash the login screen
 * (session hint lost) and fall back to language re-detection. Neither is data
 * loss — the refresh-token cookie and server-side `preferred_locale` still
 * work — but the migration keeps the experience seamless.
 *
 * This file runs its side effect at import time, so it MUST be imported in
 * `index.tsx` BEFORE `./i18n` (which reads the language key during bootstrap)
 * and before the React tree mounts (AuthContext reads the session key).
 *
 * Safe to delete this file and its import once enough time has passed that
 * effectively no users still hold the legacy keys.
 */
const RENAMES: ReadonlyArray<readonly [string, string]> = [
	['tawreed.session.user', 'sparts.session.user'],
	['tawreed.lang', 'sparts.lang'],
];

(function migrateLegacyStorageKeys(): void {
	if (typeof window === 'undefined' || !window.localStorage) return;
	for (const [oldKey, newKey] of RENAMES) {
		try {
			const legacy = window.localStorage.getItem(oldKey);
			if (legacy === null) continue;
			// Don't clobber a value already written under the new key.
			if (window.localStorage.getItem(newKey) === null) {
				window.localStorage.setItem(newKey, legacy);
			}
			window.localStorage.removeItem(oldKey);
		} catch {
			// localStorage can throw in private-mode / quota edge cases — ignore.
		}
	}
})();

export {};
